import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import Papa from 'papaparse';
import { EmptyState } from '@/components/page-header';
import { VatReportShell, rangeSearch } from '@/components/vat-report';
import { api } from '@/lib/api';
import { formatTrn } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import { useOrgId } from '@/lib/org';
import { downloadCsv, drillBoxes, type VatLine } from '@/lib/vat';

interface VatDetail {
  orgName: string;
  trn: string | null;
  from: string;
  to: string;
  currency: string;
  groups: Array<{
    box: string;
    label: string;
    lines: VatLine[];
    totalAmount: string;
    totalVat: string;
  }>;
  generatedAt: string;
}

type Search = { from: string; to: string; box?: string; code?: string };

export const Route = createFileRoute('/reports/vat-detail')({
  component: VatDetailReport,
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...rangeSearch(s),
    ...(typeof s['box'] === 'string' ? { box: s['box'] } : {}),
    ...(typeof s['code'] === 'string' ? { code: s['code'] } : {}),
  }),
});

/** VAT detail (R2 `vat-detail`): every posted line behind each VAT 201 box; the drill-down target. */
function VatDetailReport() {
  const orgId = useOrgId();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery({
    queryKey: ['vat', orgId, 'report', 'detail', search.from, search.to],
    queryFn: () =>
      api<VatDetail>(`/orgs/${orgId}/vat/reports/detail?from=${search.from}&to=${search.to}`),
    enabled: Boolean(orgId),
  });
  const boxes = search.box ? new Set(drillBoxes(search.box)) : null;
  const groups = (q.data?.groups ?? [])
    .filter((g) => !boxes || boxes.has(g.box))
    .map((g) =>
      search.code ? { ...g, lines: g.lines.filter((l) => l.taxCode === search.code) } : g,
    )
    .filter((g) => g.lines.length > 0);
  const exportCsv = () => {
    const rows: string[][] = [
      [
        'Box',
        'Date',
        'Transaction type',
        'No.',
        'Name',
        'TRN',
        'Memo/Description',
        'VAT code',
        'Amount',
        'VAT',
      ],
    ];
    for (const g of groups)
      for (const l of g.lines)
        rows.push([
          g.box,
          displayDate(l.date),
          sourceLabel(l.sourceType),
          l.entryNo,
          l.contactName ?? '',
          l.contactTrn ?? '',
          l.description ?? l.memo ?? '',
          l.taxCode,
          l.amount,
          l.vat,
        ]);
    downloadCsv(`VAT detail ${search.from} to ${search.to}.csv`, Papa.unparse(rows));
  };
  const filters = [
    search.box ? `box ${search.box}` : null,
    search.code ? `VAT code ${search.code}` : null,
  ].filter(Boolean);

  return (
    <VatReportShell
      title="VAT detail"
      orgName={q.data?.orgName}
      trn={q.data ? formatTrn(q.data.trn) : null}
      from={search.from}
      to={search.to}
      onRun={(r) => void navigate({ search: { ...search, ...r } })}
      onExport={q.data ? exportCsv : undefined}
      generatedAt={q.data?.generatedAt}
      note={
        filters.length ? (
          <p className="mb-3 text-sm print:hidden">
            Showing {filters.join(' and ')}.{' '}
            <Link
              to="/reports/vat-detail"
              search={{ from: search.from, to: search.to }}
              className="text-primary hover:underline"
            >
              Show all boxes
            </Link>
          </p>
        ) : null
      }
    >
      {!orgId ? (
        <EmptyState title="Select a client" />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <EmptyState title="Could not run the report" hint={q.error.message} />
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No VAT transactions in this period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 text-left font-medium">Date</th>
                <th className="py-2 text-left font-medium">Transaction type</th>
                <th className="py-2 text-left font-medium">No.</th>
                <th className="py-2 text-left font-medium">Name</th>
                <th className="py-2 text-left font-medium">Memo/Description</th>
                <th className="py-2 text-left font-medium">VAT code</th>
                <th className="w-[130px] py-2 text-right font-medium">Amount</th>
                <th className="w-[120px] py-2 text-right font-medium">VAT</th>
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.box} data-box={g.box}>
                <tr>
                  <td colSpan={8} className="pt-4 pb-1 font-semibold">
                    Box {g.box} · {g.label}
                  </td>
                </tr>
                {g.lines.map((l) => (
                  <tr key={`${l.lineId}-${l.box}`} className="border-b border-dashed">
                    <td className="py-1.5">{displayDate(l.date)}</td>
                    <td className="py-1.5">{sourceLabel(l.sourceType)}</td>
                    <td className="py-1.5">
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: l.entryId }}
                        className="text-primary hover:underline"
                      >
                        {l.entryNo}
                      </Link>
                    </td>
                    <td className="py-1.5">
                      {l.contactName ?? ''}
                      {l.contactTrn ? (
                        <div className="tnum text-xs text-muted-foreground">
                          TRN {formatTrn(l.contactTrn)}
                        </div>
                      ) : null}
                    </td>
                    <td className="max-w-[320px] truncate py-1.5">
                      {l.description ?? l.memo ?? ''}
                    </td>
                    <td className="py-1.5">{l.taxCode}</td>
                    <td className="tnum py-1.5 text-right">{formatAmount(l.amount)}</td>
                    <td className="tnum py-1.5 text-right">{formatAmount(l.vat)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={6} className="py-1.5">
                    Total box {g.box}
                  </td>
                  <td className="tnum border-t py-1.5 text-right">
                    {formatAmount(sum(g.lines, 'amount'))}
                  </td>
                  <td className="tnum border-t py-1.5 text-right">
                    {formatAmount(sum(g.lines, 'vat'))}
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        </div>
      )}
    </VatReportShell>
  );
}

function sum(lines: VatLine[], k: 'amount' | 'vat'): string {
  let total = 0n;
  for (const l of lines) {
    const neg = l[k].startsWith('-');
    const [w, f = ''] = (neg ? l[k].slice(1) : l[k]).split('.');
    const v = BigInt(w!) * 100n + BigInt(f.padEnd(2, '0').slice(0, 2));
    total += neg ? -v : v;
  }
  const neg = total < 0n;
  const a = neg ? -total : total;
  return `${neg ? '-' : ''}${a / 100n}.${String(a % 100n).padStart(2, '0')}`;
}
